import { DateTime } from "luxon";
import { v4 as uuid } from "uuid";

export const formatDashboardData = (data: any) => {
  let formattedData: any = {
    stats: [
      {
        id: uuid(),
        value: data?.total_sites,
        widgetKey: "total_sites",
        title: "Sites",
        desc: "Total Sites",
        color: "#4A1D96",
      },
      {
        id: uuid(),
        value: data?.total_participants,
        widgetKey: "total_participants",
        title: "Participants",
        desc: "Total Participants",
        color: "#0694A2",
      },
      {
        id: uuid(),
        value: data?.total_adverse_events,
        widgetKey: "total_adverse_events",
        title: "Adverse Events",
        desc: "Total Adverse Events",
        color: "#0E9F6E",
      },
      {
        id: uuid(),
        value: data?.total_queries,
        widgetKey: "total_queries",
        title: "Queries",
        desc: "Total queries",
        color: "#4A1D96",
      },
      {
        id: uuid(),
        value: data?.total_locked_participants,
        widgetKey: "total_locked_participants",
        title: "Locked Participants",
        desc: "Total Locked Participants",
        color: "#3F83F8",
      },
      {
        id: uuid(),
        value: data?.total_locked_forms,
        widgetKey: "total_locked_forms",
        title: "Locked Forms",
        desc: "Total Locked Forms",
        color: "#9061F9",
      },
      {
        id: uuid(),
        value: data?.total_open_queries,
        widgetKey: "total_open_queries",
        title: "Queries Pending",
        desc: "Queries Pending",
        color: "#0694A2",
      },
      {
        id: uuid(),
        value: data?.average_time_to_close_queries,
        widgetKey: "average_time_to_close_queries",
        title: "Average Time to Close",
        desc: "Average Time to Close",
        color: "#0E9F6E",
      },
      {
        id: uuid(),
        value: data?.average_open_queries_per_site,
        widgetKey: "average_open_queries_per_site",
        title: "Average Pending Per Site",
        desc: "Average Pending Per Site",
        color: "#F98080",
      },
    ].filter((item) => item.value !== null && item.value !== undefined),
    participantInsights: data?.participant_insights?.map((item: any) => {
      return {
        siteId: item?.siteId ?? "-",
        siteName: item?.siteName ?? "-",
        total: item?.total_participants ?? "-",
        included: item?.total_included_participants ?? "-",
        excluded: item?.total_excluded_participants ?? "-",
        randomized: item?.total_randomized_participants ?? "-",
      };
    }),
    participantCreationOverTime: data?.participant_creation_over_time
      ? formatParticipantGraph(data?.participant_creation_over_time)
      : null,
    participantsBySite: data?.participants_by_site?.map((item: any) => {
      return {
        siteId: item?.siteId ?? "-",
        siteName: item?.siteName ?? "-",
        totalParticipants: item?.total_participants ?? "-",
      };
    }),
    queriesBySite: data?.queries_by_site
      ? formatQueriesData(data?.queries_by_site)
      : null,
    randomizedParticipantsBySite: data?.randomized_participants_by_site?.map(
      (item: any) => {
        return {
          siteId: item?.siteId ?? "-",
          siteName: item?.siteName ?? "-",
          totalParticipants: item?.total_randomized_participants ?? "-",
        };
      }
    ),
    participantsList3Months: data?.participant_list_for_3month_view_reminders
      ? formatParticipantList(data?.participant_list_for_3month_view_reminders)
      : null,
    participantsList6Months: data?.participant_list_for_6month_view_reminders
      ? formatParticipantList(data?.participant_list_for_6month_view_reminders)
      : null,
    participantsList12Months: data?.participant_list_for_12month_view_reminders
      ? formatParticipantList(data?.participant_list_for_12month_view_reminders)
      : null,
    surveyPackages: data?.survey_package_completion?.map(
      (surveyPackage: any) => {
        return {
          id: surveyPackage?.package_id ?? "-",
          name: surveyPackage?.package_name ?? "-",
          completed: surveyPackage?.completed ?? "-",
          inProgress: surveyPackage?.in_progress ?? "-",
        };
      }
    ),
    queryOverview: data?.queries_overview
      ? formatQueriesData(data?.queries_overview)
      : null,
    maxQueryY: data?.queries_overview
      ? findQueryYMax(data?.queries_overview)
      : 5,
    isEmpty: data?.queries_overview
      ? findQueryIsEmpty(data?.queries_overview)
      : true,
    mostQueriedForms: data?.form_rank_by_queries?.map((form: any) => ({
      id: uuid(),
      name: form?.name,
      totalQueries: form?.query_count,
    })),
  };
  return formattedData;
};

const formatParticipantList = (data: any) => {
  let formattedData: any[] = [];
  const transformedData =
    data?.map((item: any) => ({
      id: item.id,
      subjectId: item.subject_id,
      textValue: item.text_value,
      diffDays: item.diff_days,
    })) || [];

  for (let i = 0; i < transformedData.length; i += 3) {
    let row = transformedData.slice(i, i + 3);
    while (row.length < 3) {
      row.push({
        id: "",
        subjectId: "",
        textValue: "",
        diffDays: "",
      });
    }

    formattedData.push(row);
  }
  return formattedData;
};

const formatQueriesData = (data: any[]) => {
  let formattedData: any[] = [];
  if (data?.length === 0) return formattedData;
  formattedData = data?.map((item: any) => ({
    siteId: item?.siteId,
    siteName: item?.siteName,
    totalQueries: item?.total_queries,
    Open: item?.total_open_queries,
    Closed: item?.total_closed_queries,
    Resolved: item?.total_resolved_queries,
    averageCloseTime: item?.average_query_close_time,
  }));
  return formattedData;
};

const findQueryYMax = (data: any[]): number => {
  let max = 5;
  const maxOpen = Math.max(...data.map((item) => item.total_open_queries));
  const maxClosed = Math.max(...data.map((item) => item.total_closed_queries));
  const maxResolved = Math.max(
    ...data.map((item) => item.total_resolved_queries)
  );
  max = Math.max(maxOpen, maxClosed, maxResolved);
  return max > 5 ? max + 1 : 5;
};

const findQueryIsEmpty = (data: any[]): boolean => {
  let isEmpty = true;
  if (data?.length === 0) return isEmpty;
  const totalQueryCount = data?.reduce(
    (acc, item) => acc + item.total_queries,
    0
  );
  if (totalQueryCount > 0) {
    isEmpty = false;
  }
  return isEmpty;
};

export const formatParticipantGraph = (data: any[]) => {
  let formattedData: any[] = [
    {
      data: [],
      id: "",
    },
  ];
  let days: { [key: string]: number } = {};

  if (data?.length === 0) return formattedData;
  const start = DateTime.fromFormat(
    data?.[0]?.date_time?.split("T")?.[0],
    "yyyy-LL-dd"
  );
  let end = DateTime.now()?.startOf("month");

  data?.forEach((item: any) => {
    const date = item?.date_time?.split("T")?.[0];
    if (date) {
      days[date] = item?.count;
    }
  });

  const n = data?.length < 3 ? 3 : end.diff(start, "months").get("months");

  const newData = [];
  for (let i = 0; i <= n; i++) {
    const key = end.toFormat("yyyy-LL-dd");
    const count = days?.[key] ?? 0;

    newData.push({
      x: end.toFormat("yyyy-LL-dd"),
      y: count,
    });

    end = end.minus({ months: 1 });
  }

  const reversedData = newData.slice().reverse();

  formattedData = [
    {
      data: reversedData,
      id: "Participants",
    },
  ];
  return formattedData;
};

export const DashboardCards = [
  {
    key: "total_onboarded",
    value: 0,
    title: "Onboarded",
    desc: "Total Onboarded",
    color: "#FFCA54",
  },
  {
    key: "total_consents_signed",
    value: 0,
    title: "Consent",
    desc: "Total Consented",
    color: "#9DA0C4",
  },
  {
    key: "total_enrolled",
    value: 0,
    title: "Enrolled",
    desc: "Total Enrolled",
    color: "#84E1BC",
  },
  {
    key: "total_scheduled_appointments",
    value: 0,
    title: "Appointments",
    desc: "Total Appointments",
    color: "#F98080",
  },
  {
    key: "total_screened_responses",
    value: 0,
    title: "Screened Responses",
    desc: "Total Screened Responses",
    color: "#3F83F8",
  },
];

export const CardTypes = [
  "total_onboarded",
  "total_consents_signed",
  "total_enrolled",
  "total_scheduled_appointments",
  "total_screened_responses",
];
export const findYMax = (data: any) => {
  const numbers = data?.[0]?.data;
  let max = 10;
  for (let i = 0; i < numbers?.length; i++) {
    if (numbers?.[i]?.y > max) {
      max = numbers?.[i]?.y;
    }
  }
  return max;
};

export const groupPieColors = [
  "#1C64F2",
  "#E74694",
  "#0E9F6E",
  "#7E3AF2",
  "#E3A008",
  "#16BDCA",
  "#FF8A4C",
];

export const formatGroupPieData = (data: any[]) => {
  const totalCount = data?.reduce(
    (sum: number, group: any) => sum + group.count,
    0
  );
  const formattedPieData = data?.map((item: any, index: number) => {
    return {
      id: (index + 1).toString(),
      label: item?.label,
      value: item?.countPercentage || 0,
      valueLabel: item?.count || 0,
      color: groupPieColors?.[index],
    };
  });
  return {
    data: formattedPieData,
    title: "Total People Across Groups",
    totalCount,
  };
};

export const formatFunnelPieData = (data: any) => {
  const funnelData = [
    {
      id: "1",
      label: "Screened",
      key: "total_screened_responses",
      color: "#1C64F2",
    },
    {
      id: "2",
      label: "Consented",
      key: "total_consents_signed",
      color: "#0E9F6E",
    },
    {
      id: "3",
      label: "Onboarded",
      key: "total_onboarded",
      color: "#7E3AF2",
    },
    {
      id: "4",
      label: "Enrolled",
      key: "total_enrolled",
      color: "#E74694",
    },
  ];
  const totalFunnelCount = funnelData.reduce(
    (sum: number, item: any) => sum + (data?.[item.key] ?? 0),
    0
  );
  const funnelPieData = funnelData.map((item: any) => {
    const count = data?.[item.key] ?? 0;
    const percentage = totalFunnelCount ? (count / totalFunnelCount) * 100 : 0;

    return {
      id: item.id,
      label: item.label,
      value: Number(percentage?.toFixed(2)),
      valueLabel: count,
      color: item.color,
    };
  });
  return {
    data: funnelPieData,
    totalParticipants: (data?.total_participants).toLocaleString(),
    totalFunnelCount,
    title: "Participant Funnel",
  };
};

export const CountCards = [
  {
    key: "totalStudies",
    value: 0,
    title: "Total Studies",
  },
  {
    key: "totalRegisteredParticipants",
    value: 0,
    title: "Total Participants in Registry",
  },
  {
    key: "totalActiveStudies",
    value: 0,
    title: "Active Studies",
  },
  {
    key: "totalRegistryTemplates",
    value: 0,
    title: "Total Registry Templates",
  },
  {
    key: "totalConsentTemplates",
    value: 0,
    title: "Consent Templates",
  },
  {
    key: "totalScreenerTemplates",
    value: 0,
    title: "Screener Templates",
  },
  {
    key: "totalDataSheetTemplates",
    value: 0,
    title: "Data sheet Templates",
  },
];
